/**
 * PHARMAKON — Pharmacy Registration / Onboarding Page
 *
 * Multi-step form:
 *   Step 1 — Pharmacy identity (nom, adresse, wilaya, agrément, CNAS code)
 *   Step 2 — Admin account (nom, prenom, email, password)
 *   Step 3 — Documents (agrément PDF, RC PDF, carte professionelle PDF — required)
 *   Step 4 — Confirmation (summary + submit)
 *
 * POST /api/v1/auth/register-pharmacie (new public endpoint needed on server)
 * The account is created with statut_compte = EN_ATTENTE and awaits admin validation.
 */
import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { T, BASE_CSS, ICONS } from "../../config/theme.js";
import { WILAYAS, STEPS, REQUIRED_DOCS } from "../../config/app.config.js";
import { adminApi } from "../../services/index.js";
import { useThemeStore, isDarkTheme } from "../../stores/themeStore.js";

/* ─── CSS FACTORY ─────────────────────────────────────────── */
const css = (c) => `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar-thumb{background:${c.borderHi};border-radius:2px}
body{font-family:'Outfit',sans-serif;background:${c.bg};color:${c.t0};min-height:100vh;}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}

.reg-shell{min-height:100vh;background:${c.bg};display:flex;flex-direction:column;}

/* ── Topbar ── */
.reg-top{
  height:58px;padding:0 32px;display:flex;align-items:center;justify-content:space-between;
  background:${c.surf};border-bottom:1px solid ${c.border};flex-shrink:0;
  position:sticky;top:0;z-index:10;
}
.reg-logo{display:flex;align-items:center;gap:10px;}
.reg-logo-mark{
  width:36px;height:36px;border-radius:10px;
  background:${c.bg};
  border:1.5px solid ${c.t0};display:flex;align-items:center;justify-content:center;
}
.reg-logo-name{font-size:16px;font-weight:800;color:${c.t0};letter-spacing:-.5px;}
.reg-logo-name span{color:${c.accent};}
.reg-top-link{font-size:12px;color:${c.t2};display:flex;align-items:center;gap:6px;}
.reg-top-link a{color:${c.accent};font-weight:700;text-decoration:none;padding:6px 14px;
  border:1px solid ${c.borderHi};border-radius:8px;transition:all 120ms;}
.reg-top-link a:hover{background:${c.accentDim};}

/* ── Layout ── */
.reg-body{
  flex:1;display:flex;gap:0;
  max-width:1020px;margin:0 auto;width:100%;padding:48px 24px;
}

/* ── Sidebar ── */
.reg-sidebar{width:220px;flex-shrink:0;padding-right:36px;}
.sidebar-heading{
  font-size:9.5px;font-weight:800;color:${c.t3};text-transform:uppercase;
  letter-spacing:1px;margin-bottom:20px;
}
.step-item{
  display:flex;align-items:flex-start;gap:12px;
  padding:10px 0;position:relative;
}
.step-item:not(:last-child)::after{
  content:'';position:absolute;left:14px;top:40px;
  width:1.5px;height:calc(100% - 10px);
  background:${c.border};
}
.step-num{
  width:30px;height:30px;border-radius:50%;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  font-size:11px;font-weight:800;transition:all 200ms;
  position:relative;z-index:1;
}
.step-num.done{background:${c.accent};color:#fff;box-shadow:0 0 0 3px ${c.accentDim};}
.step-num.active{background:${c.accentDim};border:2px solid ${c.accent};color:${c.accent};}
.step-num.todo{background:${c.raised};border:1px solid ${c.border};color:${c.t3};}
.step-text{padding-top:5px;}
.step-label{font-size:12px;font-weight:700;color:${c.t2};transition:color 150ms;line-height:1;}
.step-label.active{color:${c.t0};}
.step-label.done{color:${c.t1};}
.step-sub{font-size:10px;color:${c.t3};margin-top:3px;}

/* Info block */
.sidebar-info{
  margin-top:32px;padding:14px 16px;background:${c.raised};
  border-radius:12px;border:1px solid ${c.border};
}
.sidebar-info-head{
  font-size:10px;font-weight:800;color:${c.accent};
  text-transform:uppercase;letter-spacing:.6px;margin-bottom:8px;
  display:flex;align-items:center;gap:5px;
}
.sidebar-info p{font-size:11px;color:${c.t2};line-height:1.7;}
.sidebar-info strong{color:${c.t1};}

/* ── Main card ── */
.reg-main{flex:1;animation:fadeUp 220ms ease;}
.reg-card{
  background:${c.surf};border:1px solid ${c.border};border-radius:18px;
  padding:36px;box-shadow:${c.shadow};
}
.card-title{font-size:22px;font-weight:800;color:${c.t0};letter-spacing:-.5px;margin-bottom:4px;}
.card-sub{font-size:13px;color:${c.t2};margin-bottom:32px;line-height:1.5;}

/* ── Fields ── */
.frow{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px;}
.frow.one{grid-template-columns:1fr;}
.frow.three{grid-template-columns:1fr 1fr 1fr;}
.fld{display:flex;flex-direction:column;gap:6px;}
.fld-label{
  font-size:9.5px;font-weight:800;color:${c.t3};
  text-transform:uppercase;letter-spacing:.8px;display:flex;align-items:center;gap:4px;
}
.fld-label .req{color:${c.accent};}
.inp{
  width:100%;padding:10px 14px;
  background:${c.raised};border:1px solid ${c.border};border-radius:10px;
  font-size:13.5px;color:${c.t0};font-family:'Outfit',sans-serif;outline:none;
  transition:border-color 120ms,box-shadow 120ms;
}
.inp:focus{border-color:${c.accent};box-shadow:0 0 0 3px ${c.accentDim};}
.inp::placeholder{color:${c.t3};}
.inp.err{border-color:${c.red};box-shadow:0 0 0 3px ${c.redDim};}
select.inp{cursor:pointer;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23638F73' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;padding-right:34px;}
.inp.mono{font-family:'JetBrains Mono',monospace;font-size:12.5px;}
.fld-hint{font-size:10.5px;color:${c.t3};line-height:1.4;}
.fld-err{font-size:10.5px;color:${c.red};font-weight:600;display:flex;align-items:center;gap:4px;}

/* ── Divider ── */
.section-divider{
  margin:20px 0 16px;padding-top:20px;border-top:1px solid ${c.border};
  font-size:10px;font-weight:800;color:${c.t3};text-transform:uppercase;letter-spacing:.8px;
}

/* ── Password ── */
.pw-wrap{position:relative;}
.pw-wrap .inp{padding-right:42px;}
.pw-eye{
  position:absolute;right:12px;top:50%;transform:translateY(-50%);
  background:none;border:none;cursor:pointer;color:${c.t2};
  display:flex;align-items:center;padding:0;transition:color 120ms;
}
.pw-eye:hover{color:${c.t1};}

/* ── Strength bar ── */
.pw-strength{display:flex;gap:4px;margin-top:6px;}
.pw-seg{height:3px;flex:1;border-radius:2px;background:${c.raised};transition:background 200ms;}

/* ── Alert box ── */
.alert-box{
  display:flex;gap:10px;padding:12px 14px;border-radius:10px;
  font-size:11.5px;line-height:1.7;margin-top:8px;
}
.alert-box.amber{background:${c.amberDim};border:1px solid ${c.amber}33;color:${c.amber};}
.alert-box.red{background:${c.redDim};border:1px solid ${c.red}33;color:${c.red};}
.alert-box.blue{background:${c.blueDim};border:1px solid ${c.blue}33;color:${c.blue};}

/* ── Documents ── */
.doc-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px;}
.doc-card{
  border:2px dashed ${c.border};border-radius:14px;padding:18px 16px;
  display:flex;flex-direction:column;gap:8px;
  cursor:pointer;transition:all 150ms;position:relative;overflow:hidden;
  background:${c.bg};
}
.doc-card:hover{border-color:${c.accent};background:${c.accentDim};transform:translateY(-1px);}
.doc-card.uploaded{
  border-style:solid;border-color:${c.accent};
  background:${c.accentDim};
}
.doc-card.has-err{border-color:${c.red};}
.doc-badge{
  position:absolute;top:10px;right:10px;
  background:${c.raised};color:${c.t3};
  font-size:9px;font-weight:800;padding:2px 7px;border-radius:4px;
  text-transform:uppercase;letter-spacing:.5px;
}
.doc-badge.required{background:${c.accentDim};color:${c.accent};}
.doc-icon{
  width:38px;height:38px;border-radius:10px;
  display:flex;align-items:center;justify-content:center;
  background:${c.raised};border:1px solid ${c.border};flex-shrink:0;
}
.doc-card.uploaded .doc-icon{background:${c.accentDim};border-color:${c.accent};}
.doc-name{font-size:12.5px;font-weight:700;color:${c.t0};line-height:1.3;}
.doc-hint{font-size:10.5px;color:${c.t3};line-height:1.4;}
.doc-file-name{
  font-size:10px;color:${c.accent};font-weight:700;
  display:flex;align-items:center;gap:4px;margin-top:2px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px;
}
.doc-upload-cta{font-size:10.5px;color:${c.t3};margin-top:2px;}

/* ── Summary ── */
.summary-block{
  background:${c.raised};border-radius:12px;
  padding:16px 18px;margin-bottom:14px;
}
.summary-block-head{
  font-size:9.5px;font-weight:800;color:${c.t3};
  text-transform:uppercase;letter-spacing:.8px;margin-bottom:12px;
  padding-bottom:8px;border-bottom:1px solid ${c.border};
}
.sum-row{
  display:flex;justify-content:space-between;align-items:center;
  padding:7px 0;border-bottom:1px solid ${c.border};
}
.sum-row:last-child{border-bottom:none;}
.sum-lbl{font-size:11px;color:${c.t2};font-weight:500;}
.sum-val{font-size:12px;color:${c.t0};font-weight:600;text-align:right;max-width:55%;display:flex;align-items:center;gap:8px;}
.preview-btn{
  font-size:10px;font-weight:700;color:${c.accent};
  background:${c.accentDim};border:1px solid ${c.accent}33;
  padding:2px 8px;border-radius:5px;cursor:pointer;white-space:nowrap;
  transition:all 120ms;
}
.preview-btn:hover{background:${c.accent};color:#fff;}

/* ── Success ── */
.success-wrap{text-align:center;padding:16px 0;animation:fadeUp 300ms ease;}
.success-circle{
  width:72px;height:72px;border-radius:50%;margin:0 auto 20px;
  background:${c.accentDim};border:2px solid ${c.accent};
  display:flex;align-items:center;justify-content:center;
}
.success-title{font-size:24px;font-weight:800;color:${c.t0};margin-bottom:8px;}
.success-sub{font-size:13px;color:${c.t2};line-height:1.7;max-width:400px;margin:0 auto 24px;}
.next-steps{
  background:${c.raised};border:1px solid ${c.border};
  border-radius:12px;padding:16px 20px;text-align:left;margin-bottom:24px;
}
.next-steps-head{
  font-size:10px;font-weight:800;color:${c.accent};
  text-transform:uppercase;letter-spacing:.7px;margin-bottom:10px;
}
.next-step-item{
  font-size:12px;color:${c.t1};padding:5px 0;line-height:1.6;
  display:flex;align-items:flex-start;gap:8px;border-bottom:1px solid ${c.border};
}
.next-step-item:last-child{border-bottom:none;}

/* ── Navigation ── */
.nav-row{
  display:flex;align-items:center;justify-content:space-between;
  margin-top:28px;padding-top:20px;border-top:1px solid ${c.border};
}
.btn-back{
  background:${c.raised};border:1px solid ${c.border};color:${c.t1};
  padding:10px 20px;border-radius:10px;font-family:'Outfit',sans-serif;
  font-size:13px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;
  transition:all 120ms;
}
.btn-back:hover{border-color:${c.borderHi};color:${c.t0};}
.btn-next{
  background:${c.accent};border:none;color:#fff;
  padding:10px 26px;border-radius:10px;font-family:'Outfit',sans-serif;
  font-size:13px;font-weight:700;cursor:pointer;display:flex;align-items:center;gap:7px;
  transition:all 120ms;
}
.btn-next:hover{filter:brightness(1.08);box-shadow:0 4px 16px ${c.accentGlow};}
.btn-next:disabled{background:${c.raised};color:${c.t3};cursor:not-allowed;box-shadow:none;filter:none;}
.step-counter{font-size:11px;color:${c.t3};}

/* ── Type selector ── */
.type-shell{
  min-height:100vh;background:${c.bg};display:flex;flex-direction:column;
  align-items:center;justify-content:center;padding:32px 24px;
}
.type-header{text-align:center;margin-bottom:40px;}
.type-headline{
  font-size:32px;font-weight:900;color:${c.t0};letter-spacing:-.8px;margin-bottom:10px;
}
.type-headline span{color:${c.accent};}
.type-sub{font-size:14px;color:${c.t2};line-height:1.6;}
.type-grid{
  display:grid;grid-template-columns:1fr 1fr;gap:18px;
  max-width:660px;width:100%;
}
.type-card{
  padding:28px 24px;border:2px solid ${c.border};border-radius:18px;
  background:${c.surf};cursor:pointer;text-align:left;
  transition:all 160ms;font-family:'Outfit',sans-serif;
  box-shadow:${c.shadow};
}
.type-card:hover{border-color:var(--tc-color);box-shadow:0 0 0 4px color-mix(in srgb,var(--tc-color) 15%,transparent),${c.shadowUp};}
.type-icon{
  width:54px;height:54px;margin-bottom:18px;border:1.5px solid ${c.t0};
  border-radius:12px;display:flex;align-items:center;justify-content:center;
  color:${c.t0};background:${c.bg};
}
.type-icon svg{width:30px;height:30px;stroke:currentColor;stroke-width:2.6;fill:none;
  stroke-linecap:round;stroke-linejoin:round;}
.next-step-icon{width:22px;height:22px;flex:0 0 22px;display:inline-flex;align-items:center;justify-content:center;color:${c.t0};}
.next-step-icon svg{width:18px;height:18px;stroke:currentColor;stroke-width:2.6;fill:none;stroke-linecap:round;stroke-linejoin:round;}
.type-label{font-size:16px;font-weight:800;color:${c.t0};margin-bottom:6px;}
.type-desc{font-size:12px;color:${c.t2};line-height:1.6;margin-bottom:16px;}
.type-cta{
  font-size:12px;font-weight:700;
  display:flex;align-items:center;gap:5px;
}
.type-footer{margin-top:28px;font-size:12.5px;color:${c.t3};}
.type-footer a{color:${c.accent};font-weight:700;text-decoration:none;}
`;

/* ─── ICONS ──────────────────────────────────────────────── */
function Ico({ d, s = 14, strokeWidth = 2.6 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={s}
      height={s}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function BrandIcon({ kind }) {
  const paths = {
    pharmacy: [
      "M3 21h18",
      "M5 21V7l7-4 7 4v14",
      "M9 11h6",
      "M12 8v6",
      "M9 17h.01M15 17h.01",
    ],
    supplier: [
      "M3 21h18",
      "M5 21V5h9v16",
      "M14 10h5v11",
      "M8 8h3M8 12h3M8 16h3",
      "M17 14h.01M17 17h.01",
    ],
    mail: ["M3 5h18v14H3z", "m3 6 9 7 9-7"],
    search: ["a7 7 0 1 0 14 0 7 7 0 0 0-14 0", "m16 16 5 5"],
    check: ["m5 12 4 4L19 6"],
    alert: ["M12 3 2.8 20h18.4L12 3z", "M12 9v4", "M12 17h.01"],
    sun: ["M12 3v2", "M12 19v2", "M3 12h2", "M19 12h2", "m5.6 5.6 1.4 1.4", "m17 17 1.4 1.4", "m18.4 5.6-1.4 1.4", "m7 17-1.4 1.4", "M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0z"],
    moon: ["M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5 8.5 8.5 0 1 0 20.5 14.5z"],
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[kind].map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

const ICON = {
  check: "M20 6L9 17l-5-5",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8M12 9a3 3 0 100 6 3 3 0 000-6z",
  eyeOff:
    "M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22",
  file: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6",
  chevL: "M15 18l-6-6 6-6",
  chevR: "M9 18l6-6-6-6",
  info: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  doc: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  sun: "M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7m10 10 1.4 1.4m0-12.8L17 7M7 17l-1.4 1.4M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0z",
  moon: "M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5 8.5 8.5 0 1 0 20.5 14.5z",
};

/* ─── FIELD WRAPPER ──────────────────────────────────────── */
function Field({ label, required, hint, error, children }) {
  return (
    <div className="fld">
      <div className="fld-label">
        {label}
        {required && <span className="req"> *</span>}
      </div>
      {children}
      {hint && !error && <div className="fld-hint">{hint}</div>}
      {error && (
        <div className="fld-err">
          <Ico
            d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            s={11}
          />{" "}
          {error}
        </div>
      )}
    </div>
  );
}

/* ─── PASSWORD STRENGTH ──────────────────────────────────── */
function pwStrength(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

const STRENGTH_COLORS = ["#FF5252", "#F5A623", "#F5A623", "#00D264", "#00D264"];
const STRENGTH_LABELS = ["", "Faible", "Moyen", "Bon", "Fort"];

/* ─── MAIN COMPONENT ─────────────────────────────────────── */
export default function RegisterPage() {
  const navigate = useNavigate();
  // The public pages follow the display preference of the store (Novera Pro:
  // Clair / Sombre / Automatique); the old per-page dark flag is retired.
  const dark = isDarkTheme(useThemeStore((s) => s.theme));
  const toggleDark = useThemeStore((s) => s.toggleTheme);
  const [regType, setRegType] = useState(null); // null | "pharmacie"
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const fileRefs = useRef({});

  const c = T[dark ? "dark" : "light"];

  const [pharma, setPharma] = useState({
    nom: "",
    adresse: "",
    wilaya: "Alger",
    telephone: "",
    email: "",
    numero_agrement: "",
    code_cnas: "",
  });
  const [compte, setCompte] = useState({
    nom: "",
    prenom: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [docs, setDocs] = useState({
    agrement: null,
    registre: null,
    carte_pro: null,
    licence_locale: null,
  });

  const set = (setter) => (patch) => setter((p) => ({ ...p, ...patch }));

  /* ── Validation ── */
  const validate1 = () => {
    const e = {};
    if (!pharma.nom.trim()) e.nom = "Requis";
    if (!pharma.adresse.trim()) e.adresse = "Requis";
    if (!pharma.numero_agrement.trim()) e.numero_agrement = "Requis";
    if (!pharma.telephone.trim()) e.telephone = "Requis";
    setErrors(e);
    return !Object.keys(e).length;
  };
  const validate2 = () => {
    const e = {};
    if (!compte.nom.trim()) e.nom = "Requis";
    if (!compte.prenom.trim()) e.prenom = "Requis";
    if (!compte.email.includes("@")) e.email = "Email invalide";
    if (compte.password.length < 8) e.password = "Minimum 8 caractères";
    if (compte.password !== compte.confirm)
      e.confirm = "Les mots de passe ne correspondent pas";
    setErrors(e);
    return !Object.keys(e).length;
  };
  const validate3 = () => {
    const e = {};
    if (!docs.agrement) e.agrement = "Document obligatoire";
    if (!docs.registre) e.registre = "Document obligatoire";
    if (!docs.carte_pro) e.carte_pro = "Document obligatoire";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const goNext = () => {
    if (step === 1 && !validate1()) return;
    if (step === 2 && !validate2()) return;
    if (step === 3 && !validate3()) return;
    setErrors({});
    setStep((s) => s + 1);
  };
  const goBack = () => {
    setErrors({});
    setStep((s) => s - 1);
  };
  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    console.log("Starting Register");
    try {
      // Build multipart FormData
      const fd = new FormData();
      fd.append("pharmacie", JSON.stringify(pharma));
      fd.append("compte", JSON.stringify({ ...compte, confirm: undefined }));
      if (docs.agrement) fd.append("agrement", docs.agrement);
      if (docs.registre) fd.append("registre", docs.registre);
      if (docs.carte_pro) fd.append("carte_pro", docs.carte_pro);
      if (docs.licence_locale) fd.append("licence_locale", docs.licence_locale);
      console.log("About to call registerPharmace=ie");
      await adminApi.registerPharmacie(fd);
      setDone(true);
    } catch {
      setErrors({ submit: "Erreur réseau — veuillez réessayer." });
    } finally {
      setLoading(false);
    }
  };

  const pickFile = (key) => fileRefs.current[key]?.click();
  const onFile = (key) => (e) => {
    const file = e.target.files?.[0];
    if (file) setDocs((d) => ({ ...d, [key]: file }));
  };

  const stepStatus = (n) =>
    n < step ? "done" : n === step ? "active" : "todo";
  const strength = pwStrength(compte.password);

  /* ── Type selector screen ── */
  if (!regType) {
    return (
      <div className="type-shell" style={{ fontFamily: "'Outfit',sans-serif" }}>
        <style>{css(c)}</style>

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleDark}
          aria-label={dark ? "Passer au thème clair" : "Passer au thème sombre"}
          style={{
            position: "fixed",
            top: 18,
            right: 20,
            background: c.raised,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: "6px 10px",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          {<Ico d={dark ? ICON.sun : ICON.moon} s={17} />}
        </button>

        <div className="type-header">
          <div className="type-headline">
            Rejoindre <span>PHARMAKON</span>
          </div>
          <div className="type-sub">
            Sélectionnez votre type de compte pour commencer l'inscription
          </div>
        </div>

        <div className="type-grid">
          {[
            {
              id: "pharmacie",
              icon: "pharmacy",
              label: "Pharmacie",
              desc: "Officine ou pharmacie hospitalière — gestion stock, ventes CHIFA, analytics",
              color: c.accent,
              action: () => setRegType("pharmacie"),
            },
            {
              id: "fournisseur",
              icon: "supplier",
              label: "Fournisseur / Labo",
              desc: "Grossiste, distributeur ou laboratoire pharmaceutique — portail B2B & Data Synthèse",
              color: dark ? "#B08FFF" : "#6200EA",
              action: () => navigate("/register-fournisseur"),
            },
          ].map((t) => (
            <button
              key={t.id}
              className="type-card"
              style={{ "--tc-color": t.color }}
              onClick={t.action}
            >
              <span className="type-icon" aria-hidden="true"><BrandIcon kind={t.icon} /></span>
              <div className="type-label">{t.label}</div>
              <div className="type-desc">{t.desc}</div>
              <div className="type-cta" style={{ color: t.color }}>
                Commencer <Ico d={ICON.chevR} s={13} />
              </div>
            </button>
          ))}
        </div>

        <div className="type-footer">
          Déjà un compte ? <a href="/login">Se connecter</a>
        </div>
      </div>
    );
  }

  /* ── Multi-step registration ── */
  return (
    <div className="reg-shell">
      <style>{css(c)}</style>

      {/* Topbar */}
      <header className="reg-top">
        <div className="reg-logo">
          <div className="reg-logo-mark">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: c.t0 }}>
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
              />
              <path
                d="M12 7.5v9M7.5 12h9"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="reg-logo-name">
            PHARMA<span>KON</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            type="button"
            onClick={toggleDark}
            aria-label={dark ? "Passer au thème clair" : "Passer au thème sombre"}
            style={{
              background: c.raised,
              border: `1px solid ${c.border}`,
              borderRadius: 7,
              padding: "5px 9px",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            {<Ico d={dark ? ICON.sun : ICON.moon} s={17} />}
          </button>
          <div className="reg-top-link">
            Déjà un compte ? <a href="/login">Se connecter</a>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="reg-body">
        {/* Sidebar */}
        <aside className="reg-sidebar">
          <div className="sidebar-heading">Inscription pharmacie</div>
          {STEPS.map((s) => {
            const status = stepStatus(s.id);
            return (
              <div
                key={s.id}
                className="step-item"
                style={{ cursor: s.id < step ? "pointer" : "default" }}
                onClick={() => s.id < step && setStep(s.id)}
              >
                <div className={`step-num ${status}`}>
                  {status === "done" ? <Ico d={ICON.check} s={12} /> : s.id}
                </div>
                <div className="step-text">
                  <div className={`step-label ${status}`}>{s.label}</div>
                  <div className="step-sub">
                    {
                      [
                        "Identité officielle",
                        "Accès propriétaire",
                        "Justificatifs",
                        "Récapitulatif",
                      ][s.id - 1]
                    }
                  </div>
                </div>
              </div>
            );
          })}

          <div className="sidebar-info">
            <div className="sidebar-info-head">
              <Ico d={ICON.info} s={11} /> Processus
            </div>
            <p>
              Votre dossier sera examiné par notre équipe sous{" "}
              <strong>48h ouvrées</strong>. Vous recevrez un email de
              confirmation dès validation.
            </p>
          </div>
        </aside>

        {/* Card */}
        <main className="reg-main">
          <div className="reg-card">
            {/* ─── Step 1: Pharmacy identity ─── */}
            {step === 1 && (
              <>
                <div className="card-title">Identité de la pharmacie</div>
                <div className="card-sub">
                  Informations officielles de votre officine telles qu'elles
                  figurent sur votre agrément
                </div>

                <div className="frow one">
                  <Field
                    label="Nom de la pharmacie"
                    required
                    error={errors.nom}
                  >
                    <input
                      className={`inp${errors.nom ? " err" : ""}`}
                      value={pharma.nom}
                      placeholder="ex: Pharmacie El Amel"
                      onChange={(e) => set(setPharma)({ nom: e.target.value })}
                    />
                  </Field>
                </div>

                <div className="frow">
                  <Field
                    label="N° Agrément MOH"
                    required
                    hint="Format: WW/XXXX/AAAA"
                    error={errors.numero_agrement}
                  >
                    <input
                      className={`inp mono${errors.numero_agrement ? " err" : ""}`}
                      value={pharma.numero_agrement}
                      placeholder="16/0142/2019"
                      onChange={(e) =>
                        set(setPharma)({ numero_agrement: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Code pharmacien CNAS"
                    hint="Facultatif — pour le tiers payant CHIFA"
                  >
                    <input
                      className="inp mono"
                      value={pharma.code_cnas}
                      placeholder="ex: 1600142"
                      onChange={(e) =>
                        set(setPharma)({ code_cnas: e.target.value })
                      }
                    />
                  </Field>
                </div>

                <div className="frow one">
                  <Field
                    label="Adresse complète"
                    required
                    error={errors.adresse}
                  >
                    <input
                      className={`inp${errors.adresse ? " err" : ""}`}
                      value={pharma.adresse}
                      placeholder="12 Rue Didouche Mourad, Alger Centre"
                      onChange={(e) =>
                        set(setPharma)({ adresse: e.target.value })
                      }
                    />
                  </Field>
                </div>

                <div className="frow">
                  <Field label="Wilaya" required>
                    <select
                      className="inp"
                      value={pharma.wilaya}
                      onChange={(e) =>
                        set(setPharma)({ wilaya: e.target.value })
                      }
                    >
                      {WILAYAS.map((w, i) => (
                        <option key={w} value={w}>
                          {String(i + 1).padStart(2, "0")} — {w}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Téléphone" required error={errors.telephone}>
                    <input
                      className={`inp${errors.telephone ? " err" : ""}`}
                      value={pharma.telephone}
                      placeholder="0555 00 00 00"
                      onChange={(e) =>
                        set(setPharma)({ telephone: e.target.value })
                      }
                    />
                  </Field>
                </div>

                <div className="frow one">
                  <Field
                    label="Email professionnel"
                    hint="Pour les notifications de la plateforme"
                  >
                    <input
                      className="inp"
                      type="email"
                      value={pharma.email}
                      placeholder="contact@pharmacie.dz"
                      onChange={(e) =>
                        set(setPharma)({ email: e.target.value })
                      }
                    />
                  </Field>
                </div>
              </>
            )}

            {/* ─── Step 2: Admin account ─── */}
            {step === 2 && (
              <>
                <div className="card-title">Compte administrateur</div>
                <div className="card-sub">
                  Ce compte sera associé au rôle{" "}
                  <strong style={{ color: c.accent }}>PROPRIÉTAIRE</strong> —
                  accès complet à tous les modules
                </div>

                <div className="frow">
                  <Field label="Prénom" required error={errors.prenom}>
                    <input
                      className={`inp${errors.prenom ? " err" : ""}`}
                      value={compte.prenom}
                      placeholder="Mohamed"
                      onChange={(e) =>
                        set(setCompte)({ prenom: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Nom" required error={errors.nom}>
                    <input
                      className={`inp${errors.nom ? " err" : ""}`}
                      value={compte.nom}
                      placeholder="Benali"
                      onChange={(e) => set(setCompte)({ nom: e.target.value })}
                    />
                  </Field>
                </div>

                <div className="frow one">
                  <Field
                    label="Email de connexion"
                    required
                    error={errors.email}
                  >
                    <input
                      className={`inp${errors.email ? " err" : ""}`}
                      type="email"
                      value={compte.email}
                      placeholder="proprietaire@pharmacie.dz"
                      onChange={(e) =>
                        set(setCompte)({ email: e.target.value })
                      }
                    />
                  </Field>
                </div>

                <div className="frow">
                  <Field
                    label="Mot de passe"
                    required
                    hint="Minimum 8 caractères"
                    error={errors.password}
                  >
                    <div className="pw-wrap">
                      <input
                        className={`inp${errors.password ? " err" : ""}`}
                        type={showPw ? "text" : "password"}
                        value={compte.password}
                        placeholder="••••••••"
                        onChange={(e) =>
                          set(setCompte)({ password: e.target.value })
                        }
                      />
                      <button
                        className="pw-eye"
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                      >
                        <Ico d={showPw ? ICON.eyeOff : ICON.eye} s={16} />
                      </button>
                    </div>
                    {compte.password.length > 0 && (
                      <>
                        <div className="pw-strength">
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              className="pw-seg"
                              style={{
                                background:
                                  i <= strength
                                    ? STRENGTH_COLORS[strength]
                                    : c.raised,
                              }}
                            />
                          ))}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: STRENGTH_COLORS[strength],
                            fontWeight: 600,
                            marginTop: 3,
                          }}
                        >
                          {STRENGTH_LABELS[strength]}
                        </div>
                      </>
                    )}
                  </Field>
                  <Field
                    label="Confirmer le mot de passe"
                    required
                    error={errors.confirm}
                  >
                    <input
                      className={`inp${errors.confirm ? " err" : ""}`}
                      type="password"
                      value={compte.confirm}
                      placeholder="••••••••"
                      onChange={(e) =>
                        set(setCompte)({ confirm: e.target.value })
                      }
                    />
                  </Field>
                </div>

                <div className="alert-box amber">
                  <Ico
                    d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    s={16}
                  />
                  <span>
                    Ce compte sera activé uniquement après validation de votre
                    dossier par l'équipe PHARMAKON. Un email vous sera envoyé
                    dès approbation.
                  </span>
                </div>
              </>
            )}

            {/* ─── Step 3: Documents ─── */}
            {step === 3 && (
              <>
                <div className="card-title">Documents requis</div>
                <div className="card-sub">
                  Formats acceptés : PDF, JPG, PNG — max 5 Mo par fichier
                </div>

                <div className="doc-grid">
                  {REQUIRED_DOCS.map((doc) => (
                    <div key={doc.key}>
                      <input
                        type="file"
                        accept={doc.accept}
                        style={{ display: "none" }}
                        ref={(el) => (fileRefs.current[doc.key] = el)}
                        onChange={onFile(doc.key)}
                      />
                      <div
                        className={`doc-card${docs[doc.key] ? " uploaded" : ""}${errors[doc.key] ? " has-err" : ""}`}
                        onClick={() => pickFile(doc.key)}
                      >
                        <div
                          className={`doc-badge ${doc.optional ? "" : "required"}`}
                        >
                          {doc.optional ? "Optionnel" : "Requis"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "flex-start",
                          }}
                        >
                          <div className="doc-icon">
                            {docs[doc.key] ? (
                              <Ico d={ICON.check} s={18} />
                            ) : (
                              <Ico d={ICON.file} s={18} />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="doc-name">{doc.label}</div>
                            <div className="doc-hint">{doc.hint}</div>
                            {docs[doc.key] ? (
                              <div className="doc-file-name">
                                <Ico d={ICON.check} s={10} />{" "}
                                {docs[doc.key].name}
                              </div>
                            ) : (
                              <div className="doc-upload-cta">
                                Cliquer pour uploader
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {errors[doc.key] && (
                        <div className="fld-err" style={{ marginTop: 5 }}>
                          <Ico
                            d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                            s={11}
                          />
                          {errors[doc.key]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="alert-box blue" style={{ fontSize: 11 }}>
                  <Ico
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    s={16}
                  />
                  <span>
                    Les documents téléchargés seront vérifiés par notre équipe
                    de conformité. Aucune donnée sensible n'est transmise à des
                    tiers.
                  </span>
                </div>
              </>
            )}

            {/* ─── Step 4: Summary ─── */}
            {step === 4 && !done && (
              <>
                <div className="card-title">Récapitulatif</div>
                <div className="card-sub">
                  Vérifiez vos informations avant d'envoyer votre dossier
                </div>

                <div className="summary-block">
                  <div className="summary-block-head">Pharmacie</div>
                  {[
                    ["Nom", pharma.nom],
                    ["N° Agrément", pharma.numero_agrement],
                    ["Wilaya", pharma.wilaya],
                    ["Téléphone", pharma.telephone],
                    ["Email", pharma.email || "—"],
                  ].map(([l, v]) => (
                    <div key={l} className="sum-row">
                      <span className="sum-lbl">{l}</span>
                      <span className="sum-val">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="summary-block">
                  <div className="summary-block-head">
                    Compte administrateur
                  </div>
                  {[
                    ["Prénom & Nom", `${compte.prenom} ${compte.nom}`],
                    ["Email", compte.email],
                  ].map(([l, v]) => (
                    <div key={l} className="sum-row">
                      <span className="sum-lbl">{l}</span>
                      <span className="sum-val">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="summary-block">
                  <div className="summary-block-head">Documents</div>
                  {REQUIRED_DOCS.map((d) => (
                    <div key={d.key} className="sum-row">
                      <span className="sum-lbl">{d.label}</span>
                      <span
                        className="sum-val"
                        style={{
                          color: docs[d.key]
                            ? c.accent
                            : d.optional
                              ? c.t3
                              : c.red,
                        }}
                      >
                        {docs[d.key] ? (
                          <>
                            ✓ {docs[d.key].name}
                            <button
                              className="preview-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  URL.createObjectURL(docs[d.key]),
                                  "_blank",
                                );
                              }}
                            >
                              Voir
                            </button>
                          </>
                        ) : d.optional ? (
                          "— (facultatif)"
                        ) : (
                          "⚠ Manquant"
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {errors.submit && (
                  <div className="alert-box red">
                    <Ico d="M12 9v4M12 17h.01" s={16} />
                    <span>{errors.submit}</span>
                  </div>
                )}
              </>
            )}

            {/* ─── Success ─── */}
            {done && (
              <div className="success-wrap">
                <div className="success-circle">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                    <path
                      d={ICON.check}
                      stroke={c.accent}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="success-title">Dossier envoyé !</div>
                <div className="success-sub">
                  Votre demande d'inscription a bien été reçue. Notre équipe
                  l'examinera dans les{" "}
                  <strong style={{ color: c.t1 }}>48h ouvrées</strong>.
                </div>
                <div className="next-steps">
                  <div className="next-steps-head">Prochaines étapes</div>
                  {[
                    [
                      "mail",
                      "Email de confirmation avec votre numéro de dossier",
                    ],
                    ["search", "Vérification de l'authenticité de vos documents"],
                    [
                      "check",
                      "Envoi de vos identifiants d'accès en cas d'approbation",
                    ],
                    ["alert", "Contact pour régularisation si dossier incomplet"],
                  ].map(([ic, txt]) => (
                    <div key={txt} className="next-step-item">
                      <span className="next-step-icon"><BrandIcon kind={ic} /></span> {txt}
                    </div>
                  ))}
                </div>
                <button className="btn-next" onClick={() => navigate("/login")}>
                  Retour à la connexion <Ico d={ICON.chevR} s={14} />
                </button>
              </div>
            )}

            {/* ─── Navigation ─── */}
            {!done && (
              <div className="nav-row">
                <div>
                  {step > 1 && (
                    <button className="btn-back" onClick={goBack}>
                      <Ico d={ICON.chevL} s={14} /> Précédent
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span className="step-counter">
                    Étape {step} / {STEPS.length}
                  </span>
                  {step < 4 ? (
                    <button className="btn-next" onClick={goNext}>
                      Suivant <Ico d={ICON.chevR} s={14} />
                    </button>
                  ) : (
                    <button
                      className="btn-next"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <span
                          style={{
                            animation: "spin .8s linear infinite",
                            display: "inline-block",
                          }}
                        >
                          ⟳
                        </span>
                      ) : (
                        <Ico d={ICON.check} s={14} />
                      )}
                      {loading ? "Envoi en cours…" : "Soumettre le dossier"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
